<script setup lang="ts">
import type { Scenario } from '@/stores/type/scenario.type'
import { useScenarioStore } from '@/stores/scenario'
import { computed, type PropType, ref, toRef } from 'vue'
import { FavoriteButton } from '@haskoning/vue-component-library'
import { storeToRefs } from 'pinia'
import { DateTime } from 'luxon'
import router from '@/router'

import UpdateSimulationNameDialog from '@/components/scenario-index/ScenarioUpdateDialog.vue'

const isDialogOpen = ref(false)

const props = defineProps({
  item: {
    type: Object as PropType<Scenario>,
    required: true
  },
  isCheckboxShown: {
    type: Boolean,
    default: false
  }
})

const item = toRef(props, 'item')

const scenarioStore = useScenarioStore()
const { selectedItems } = storeToRefs(scenarioStore)

const statusChoices = ref<{ value: string; display_name: string }[]>([])

const isSelected = computed<boolean>({
  get: () => selectedItems.value?.includes(item.value) || false,
  set: () => {
    const index = selectedItems.value.findIndex(
      (selectedItem: Scenario) => selectedItem.id === item.value?.id
    )

    index !== -1 ? selectedItems.value.splice(index, 1) : selectedItems.value.push(item.value)
  }
})

const draftStatusValue = computed(() => {
  return statusChoices.value.find((choice) => choice.display_name === 'Draft')?.value || 'Draft'
})

const navigateToScenario = () => {
  const routeName =
    item.value?.status === draftStatusValue.value ? 'scenario-form' : 'scenario-result'

  router.push({
    name: routeName,
    params: { id: item.value?.project, scenarioId: item.value?.id }
  })
}

const updateFavorite = async () => {
  if (item.value) {
    item.value.favorite = !item.value.favorite
    await scenarioStore.update(item.value)
  }
}

const openCustomDialog = () => {
  isDialogOpen.value = true
}

const handleUpdateSimulation = async (updatedItem: Scenario) => {
  await scenarioStore.update({ id: updatedItem.id, name: updatedItem.name } as Scenario)
  item.value.name = updatedItem.name
}
</script>

<template>
  <tr v-ripple style="cursor: pointer" @click.stop="navigateToScenario">
    <td v-if="isCheckboxShown">
      <v-checkbox v-model="isSelected" density="compact" hide-details @click.stop="" />
    </td>
    <!-- Simulation Name -->
    <td>
      <a
        @click.stop="navigateToScenario"
        style="color: #009eab !important; text-decoration: none; cursor: pointer"
      >
        {{ item.name }}
      </a>
    </td>

    <!-- Calculation Type -->
    <td>{{ item.calculation_type?.join(', ') || '-' }}</td>

    <!-- Origin Type -->
    <td>{{ item.origin_type || '-' }}</td>

    <!-- Destination Type -->
    <td>{{ item.destination_type || '-' }}</td>

    <!-- Disruption Type -->
    <td>{{ item.disruption_type || '-' }}</td>

    <!-- Date Created -->
    <td>{{ DateTime.fromISO(item.created).toLocaleString(DateTime.DATETIME_MED) }}</td>

    <!-- Favorite -->
    <td class="text-center">
      <FavoriteButton :item="item" @update-favorite="updateFavorite" />
    </td>
    <td v-if="scenarioStore.canChangeScenario" class="text-center">
      <v-btn variant="text" :icon="'$edit'" @click.stop="openCustomDialog" />
    </td>
  </tr>
  <UpdateSimulationNameDialog
    :item="item"
    :isOpen="isDialogOpen"
    :help-text="scenarioStore.fields?.['name']?.help_text || ''"
    @update="handleUpdateSimulation"
    @close="isDialogOpen = false"
  />
</template>

<style scoped></style>
